import "./__styles__/Tabs.scss";
import React, { useContext } from "react";
import { map, startCase } from "lodash";
import { DOCUMENT_UPLOAD_DETAIL_TAB_NAME } from "common/constants";
import {
  CertificateUploadStatus,
  GetDocumentUploadQuery,
} from "../../generated/graphql";
import useNavigationTabs from "../../hooks/useNavigationTabs";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import TabGroup from "../Common/TabGroup";
import Errors from "./ElevationCertificates/Errors";
import ExtractedFields from "./Forms/ElevationCertificates/ExtractedFields";
import FormFields from "./Forms/GeneratedDocuments/FormFields";
import Location from "./Location";
import Settings from "./Settings";
import { useShowLocationTab } from "./useShowLocationTab";

type Account = { ecErrorDetectionEnabled: boolean };

export type DocumentUploadContextType = {
  documentUpload: NonNullable<GetDocumentUploadQuery["documentUpload"]>;
  sidebarRef: React.RefObject<HTMLDivElement>;
};

export const DocumentUploadContext =
  React.createContext<DocumentUploadContextType>({
    documentUpload: null as any,
    sidebarRef: null as any,
  });

interface TabBodyProps {
  tab?: DOCUMENT_UPLOAD_DETAIL_TAB_NAME;
  setTab: (tab: DOCUMENT_UPLOAD_DETAIL_TAB_NAME) => void;
  prevLocation: { pathname: string; search?: string } | string;
  fileUrl: string;
  documentUpload: NonNullable<GetDocumentUploadQuery["documentUpload"]>;
  sidebarRef: React.RefObject<HTMLDivElement>;
  onUpdate: () => void;
}

const TabBody = (props: TabBodyProps) => {
  return (
    <DocumentUploadContext.Provider
      value={{
        documentUpload: props.documentUpload,
        sidebarRef: props.sidebarRef,
      }}
    >
      {props.tab === DOCUMENT_UPLOAD_DETAIL_TAB_NAME.ISSUES && (
        <Errors {...props} />
      )}
      {props.tab === DOCUMENT_UPLOAD_DETAIL_TAB_NAME.EXTRACTED_FIELDS && (
        <ExtractedFields {...props} />
      )}
      {props.tab === DOCUMENT_UPLOAD_DETAIL_TAB_NAME.LOCATION &&
        props.documentUpload.certificateUpload && (
          <Location
            documentUpload={props.documentUpload}
            extractedAddress={
              props.documentUpload.certificateUpload.extractedAddress
            }
            geocodeData={props.documentUpload.certificateUpload.geocodeData}
          />
        )}
      {props.tab === DOCUMENT_UPLOAD_DETAIL_TAB_NAME.FORM_FIELDS && (
        <FormFields {...props} />
      )}
      {props.tab === DOCUMENT_UPLOAD_DETAIL_TAB_NAME.SETTINGS && (
        <Settings {...props} />
      )}
    </DocumentUploadContext.Provider>
  );
};

const generateTabs = ({
  documentUpload,
  account,
  isPublic,
  showLocationTab,
}: {
  documentUpload: NonNullable<GetDocumentUploadQuery["documentUpload"]>;
  account: Account;
  isPublic: boolean;
  showLocationTab: boolean;
}) => {
  const tabs = [];

  if (
    documentUpload.certificateUpload?.status ===
      CertificateUploadStatus.COMPLETED ||
    documentUpload.certificateUpload?.status ===
      CertificateUploadStatus.NEEDS_LOCATION
  ) {
    const lowConfidenceCount = Object.keys(
      documentUpload.certificateUpload.lowConfidence ?? {},
    ).length;
    const extractedFieldsLabel =
      lowConfidenceCount ?
        `Extracted fields (${lowConfidenceCount})`
      : "Extracted fields";
    tabs.push({
      value: DOCUMENT_UPLOAD_DETAIL_TAB_NAME.EXTRACTED_FIELDS,
      label: extractedFieldsLabel,
    });
  }

  const { ecErrorDetectionEnabled } = account;
  if (
    ecErrorDetectionEnabled &&
    documentUpload.certificateUpload &&
    documentUpload.certificateUpload.status !== CertificateUploadStatus.CANCELED
  ) {
    let label: string;
    if (
      documentUpload.certificateUpload.status ===
      CertificateUploadStatus.COMPLETED
    ) {
      const validationErrorCount =
        documentUpload.elevationCertificate?.validationErrors?.count || 0;
      label = `Issues (${validationErrorCount})`;
    } else {
      label = "Issues";
    }
    tabs.unshift({
      value: DOCUMENT_UPLOAD_DETAIL_TAB_NAME.ISSUES,
      label,
    });
  }

  if (showLocationTab) {
    tabs.push({
      value: DOCUMENT_UPLOAD_DETAIL_TAB_NAME.LOCATION,
      label: "Location",
    });
  }

  if (documentUpload.formData && documentUpload.formStructure) {
    tabs.push({
      value: DOCUMENT_UPLOAD_DETAIL_TAB_NAME.FORM_FIELDS,
      label: "Form fields",
    });
  }

  if (!isPublic) {
    tabs.push({
      value: DOCUMENT_UPLOAD_DETAIL_TAB_NAME.SETTINGS,
      label: "Settings",
    });
  }

  return tabs;
};

export default ({
  documentUpload,
  account,
  fileUrl,
  prevLocation,
  sidebarRef,
  onUpdate,
}: {
  documentUpload: NonNullable<GetDocumentUploadQuery["documentUpload"]>;
  account: Account;
  fileUrl: string;
  prevLocation: { pathname: string; search?: string } | string;
  sidebarRef: React.RefObject<HTMLDivElement>;
  onUpdate: () => void;
}) => {
  const { isPublic } = useContext(AuthContext);
  const showLocationTab = useShowLocationTab(documentUpload);

  const tabs = generateTabs({
    documentUpload,
    account,
    isPublic,
    showLocationTab,
  });
  let defaultTab: DOCUMENT_UPLOAD_DETAIL_TAB_NAME | undefined;

  if (
    documentUpload.certificateUpload?.status ===
    CertificateUploadStatus.COMPLETED
  ) {
    defaultTab =
      account.ecErrorDetectionEnabled ?
        DOCUMENT_UPLOAD_DETAIL_TAB_NAME.ISSUES
      : DOCUMENT_UPLOAD_DETAIL_TAB_NAME.EXTRACTED_FIELDS;
  } else if (
    documentUpload.certificateUpload?.status ===
      CertificateUploadStatus.PROCESSING &&
    account.ecErrorDetectionEnabled
  ) {
    defaultTab = DOCUMENT_UPLOAD_DETAIL_TAB_NAME.ISSUES;
  } else if (documentUpload.formData && documentUpload.formStructure) {
    defaultTab = DOCUMENT_UPLOAD_DETAIL_TAB_NAME.FORM_FIELDS;
  } else if (!isPublic) {
    defaultTab = DOCUMENT_UPLOAD_DETAIL_TAB_NAME.SETTINGS;
  }

  const [tab, setTab] = useNavigationTabs<
    DOCUMENT_UPLOAD_DETAIL_TAB_NAME | undefined
  >({
    defaultTab,
    allowedTabs: map(tabs, "value"),
  });

  const handleTabClick = (value: DOCUMENT_UPLOAD_DETAIL_TAB_NAME) => {
    track("Certificate Tab Clicked", {
      fromTab: startCase(tab as string),
      toTab: startCase(value),
    });

    if (
      value === DOCUMENT_UPLOAD_DETAIL_TAB_NAME.SETTINGS ||
      value === DOCUMENT_UPLOAD_DETAIL_TAB_NAME.ISSUES
    ) {
      window.location.hash = "";
      sidebarRef?.current?.scrollTo({ top: 0 });
    }

    setTab(value);
  };

  return (
    <>
      {!!tab && (
        <div styleName="tab-header" data-testid="tab-header">
          <TabGroup currentTab={tab} setTab={handleTabClick} tabs={tabs} />
        </div>
      )}

      <div styleName="tab-body">
        <TabBody
          tab={tab}
          setTab={setTab}
          documentUpload={documentUpload}
          sidebarRef={sidebarRef}
          fileUrl={fileUrl}
          prevLocation={prevLocation}
          onUpdate={onUpdate}
        />
      </div>
    </>
  );
};
